import { useCallback, useMemo, useState } from "react";
import { locationService } from "@project/services/admin/location.service";
import { useI18n } from "@project/lib/use-i18n";

export type LocationCascadeValue = {
  districtId: string;
  cityId: string;
  areaId: string;
};

export type LocationLabels = {
  districtName: string;
  cityName: string;
  areaName: string;
};

export const EMPTY_LOCATION_LABELS: LocationLabels = {
  districtName: "",
  cityName: "",
  areaName: "",
};

export function locationSeedOption(id: string, name: string) {
  return id && name ? { value: id, label: name } : undefined;
}

export async function searchDistrictOptions(query: string) {
  const result = await locationService.listDistrictsPage({
    page: 1,
    page_size: 50,
    search: query || undefined,
  });
  return result.data.map(d => ({ value: String(d.id), label: d.name }));
}

export async function searchCityOptions(districtId: string, query: string) {
  if (!districtId) {
    return [];
  }
  const result = await locationService.listCitiesPage({
    page: 1,
    page_size: 50,
    district: Number(districtId),
    search: query || undefined,
  });
  return result.data.map(c => ({ value: String(c.id), label: c.name }));
}

export async function searchAreaOptions(cityId: string, query: string) {
  if (!cityId) {
    return [];
  }
  const result = await locationService.listAreasPage({
    page: 1,
    page_size: 50,
    city: Number(cityId),
    search: query || undefined,
  });
  return result.data.map(a => ({ value: String(a.id), label: a.name }));
}

export async function resolveLocationLabels(
  ids: LocationCascadeValue,
  labels: LocationLabels
): Promise<LocationLabels> {
  const needsDistrict = Boolean(ids.districtId && !labels.districtName);
  const needsCity = Boolean(ids.cityId && !labels.cityName);
  const needsArea = Boolean(ids.areaId && !labels.areaName);

  if (!needsDistrict && !needsCity && !needsArea) {
    return labels;
  }

  const [district, city, area] = await Promise.all([
    needsDistrict
      ? locationService.getDistrict(Number(ids.districtId))
      : Promise.resolve(undefined),
    needsCity
      ? locationService.getCity(Number(ids.cityId))
      : Promise.resolve(undefined),
    needsArea
      ? locationService.getArea(Number(ids.areaId))
      : Promise.resolve(undefined),
  ]);

  return {
    districtName: district?.name ?? labels.districtName,
    cityName: city?.name ?? labels.cityName,
    areaName: area?.name ?? labels.areaName,
  };
}

const EMPTY: LocationCascadeValue = { districtId: "", cityId: "", areaId: "" };

export function useLocationCascade(initial: LocationCascadeValue = EMPTY) {
  const { t } = useI18n();
  const [value, setValue] = useState<LocationCascadeValue>(initial);

  const searchDistricts = useCallback(async (query: string) => {
    return searchDistrictOptions(query);
  }, []);

  const searchCities = useCallback(
    async (query: string) => {
      return searchCityOptions(value.districtId, query);
    },
    [value.districtId]
  );

  const searchAreas = useCallback(
    async (query: string) => {
      return searchAreaOptions(value.cityId, query);
    },
    [value.cityId]
  );

  const setDistrictId = useCallback((districtId: string) => {
    setValue({ districtId, cityId: "", areaId: "" });
  }, []);

  const setCityId = useCallback(
    (cityId: string) => {
      setValue({ districtId: value.districtId, cityId, areaId: "" });
    },
    [value.districtId]
  );

  const setAreaId = useCallback(
    (areaId: string) => {
      setValue({ districtId: value.districtId, cityId: value.cityId, areaId });
    },
    [value.districtId, value.cityId]
  );

  const cityDisabled = !value.districtId;
  const areaDisabled = !value.cityId;

  const cityPlaceholder = useMemo(
    () =>
      cityDisabled
        ? t("account.admissionForm.location.cityPlaceholderDisabled")
        : t("account.admissionForm.location.cityPlaceholder"),
    [cityDisabled, t]
  );

  const areaPlaceholder = useMemo(
    () =>
      areaDisabled
        ? t("account.admissionForm.location.areaPlaceholderDisabled")
        : t("account.admissionForm.location.areaPlaceholder"),
    [areaDisabled, t]
  );

  return {
    value,
    setValue,
    setDistrictId,
    setCityId,
    setAreaId,
    searchDistricts,
    searchCities,
    searchAreas,
    cityDisabled,
    areaDisabled,
    cityPlaceholder,
    areaPlaceholder,
  };
}
